import { useCallback, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography
} from '@mui/material';

import type { GroupSelection } from './groupFilter';
import { useBulkSetArmed } from '../../api/useEntry';
import {
  applyBulkArmedDtos,
  buildBulkArmedDtos,
  type BulkArmedSelection
} from './bulkScope';

/**
 * BULK DISARM for the opener tab — a single "Disarm all" (red) across a WHOLE
 * scope in one request via `/rules/bulk-armed` (`desiredArmed:false`). Mirrors
 * `GroupMakerBulkControls` (same scope-aware shape + confirm-dialog UX).
 *
 * ARMING is intentionally NOT here: turning a maker flag ON (PM/KM ON) already
 * arms a disarmed rule server-side (`legArm` in the opener rules service), so a
 * dedicated "Arm all" would duplicate that lever. Disarm stays because it is the
 * only CONFIG-PRESERVING pause: setting `desiredArmed:false` stops the rules
 * WITHOUT clearing which legs are configured as makers, so a later PM/KM ON
 * re-arms them with the same leg setup. (Disarming via maker-flag OFF instead
 * requires clearing every leg and wipes that configuration.)
 *
 * The control is scope-aware: a GROUP scope (a real group → scope:'all' +
 * groupId) or a concrete SUBGROUP scope (scope:'all' + subgroupId), and
 * selection-aware: when a checkbox selection is live it scopes to the checked
 * rows only (scope:'ids' over their rule ids), exactly like the PM/KM maker
 * bulk. It is DISABLED unless a concrete scope exists (a real group, a subgroup,
 * or a live selection) — never disarms the whole account from the 'all' /
 * 'ungrouped' filters, mirroring the unwinder platform bulk.
 */

export type GroupArmScope =
  | {
      kind: 'group';
      /** The page's group filter — all / a real group / ungrouped. */
      selection: GroupSelection;
      label: string | null;
      /** Group-scoped (pre-search) rule ids — only used on the Ungrouped path. */
      scopedRuleIds: string[];
    }
  | {
      kind: 'subgroup';
      subgroupId: string;
      label: string;
    };

interface GroupArmBulkControlsProps {
  scope: GroupArmScope;
  /** Honest pair total for the confirm dialog (the whole scope, not just the
   *  search-narrowed rows). */
  confirmCount: number;
  /** Rule ids behind the currently checked rows. When `selectedCount > 0` the
   *  control switches to SELECTION mode (scope:'ids' over these ids), exactly
   *  like the PM/KM maker bulk — group scope is ignored while a selection is
   *  live. */
  selectedRuleIds?: string[];
  selectedCount?: number;
}

export function GroupArmBulkControls({
  scope,
  confirmCount,
  selectedRuleIds = [],
  selectedCount = 0
}: GroupArmBulkControlsProps) {
  const bulkArmed = useBulkSetArmed();
  const [confirmDisarm, setConfirmDisarm] = useState(false);

  // Selection mode mirrors the PM/KM maker bulk: a live checkbox selection
  // takes over the Disarm button and scopes it to the checked rows only.
  const selectionMode = selectedCount > 0;

  // Concrete scope guard: only a real group, a subgroup, or a live selection
  // may disarm. The 'all' / 'ungrouped' filters never disarm the whole
  // account (mirror the unwinder platform bulk).
  const concreteScope =
    scope.kind === 'subgroup' ||
    (scope.kind === 'group' && scope.selection.kind === 'group');
  const canBulk = selectionMode || concreteScope;

  const labelSx = {
    fontSize: '0.62rem',
    color: 'grey.500',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.03em',
    whiteSpace: 'nowrap' as const
  };
  const btnSx = { fontSize: '0.62rem', py: 0.1, px: 0.8, minWidth: 0 } as const;

  const run = useCallback(async () => {
    const selection: BulkArmedSelection = selectionMode
      ? { kind: 'selected', ruleIds: selectedRuleIds }
      : scope.kind === 'subgroup'
        ? { kind: 'subgroup', subgroupId: scope.subgroupId }
        : scope.selection;
    const scopedRuleIds =
      !selectionMode && scope.kind === 'group' ? scope.scopedRuleIds : [];
    const dtos = buildBulkArmedDtos(selection, false, scopedRuleIds);
    if (dtos.length === 0) return;
    await applyBulkArmedDtos(dtos, dto => bulkArmed.mutateAsync(dto));
  }, [scope, bulkArmed, selectionMode, selectedRuleIds]);

  // Honest count + noun for the confirm dialog — the checked subset when a
  // selection is live, otherwise the whole group/subgroup scope.
  const effectiveCount = selectionMode ? selectedCount : confirmCount;
  const scopeNoun = selectionMode
    ? 'the selected pairs'
    : scope.kind === 'subgroup'
      ? scope.label
      : scope.label ?? 'this group';

  return (
    <>
      {bulkArmed.isLoading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <CircularProgress size={13} thickness={5} />
          <Typography sx={{ ...labelSx, color: 'grey.300' }}>
            Applying — disarm…
          </Typography>
        </Box>
      ) : (
        <>
          {selectionMode && (
            <Typography sx={{ ...labelSx, color: 'grey.300' }}>
              {selectedCount} selected
            </Typography>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Button
              size="small"
              color="error"
              variant="outlined"
              sx={btnSx}
              disabled={bulkArmed.isLoading || !canBulk}
              onClick={() => setConfirmDisarm(true)}
            >
              Disarm all
            </Button>
          </Box>
        </>
      )}

      <Dialog open={confirmDisarm} onClose={() => setConfirmDisarm(false)}>
        <DialogTitle sx={{ fontSize: '0.95rem' }}>Disarm {scopeNoun}?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '0.85rem' }}>
            This disarms {effectiveCount} pair
            {effectiveCount === 1 ? '' : 's'} in {scopeNoun}. Disarming pauses the
            rules and stops new orders; the maker-leg configuration is preserved.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDisarm(false)}>Cancel</Button>
          <Button
            color="error"
            onClick={() => {
              void run();
              setConfirmDisarm(false);
            }}
            autoFocus
          >
            Disarm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
